import { useLayoutEffect, useRef, useState } from 'react';
import { PRICE_FILTER_SLIDER_THRESHOLD } from '../consts/priceFilters';

const SCROLL_EDGE_TOLERANCE_PX = 1;
const ROW_FIT_RESERVE_PX = 1;
const MIN_COMPACT_GAP_PX = 1;
const FONT_FIT_ITERATIONS = 8;

export function usePriceFilterScroll(isMobile: boolean, itemCount: number) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const regularMeasureRef = useRef<HTMLDivElement>(null);
  const compactMeasureRef = useRef<HTMLDivElement>(null);
  const [scrollState, setScrollState] = useState({
    isCompact: false,
    compactPaddingInline: 0,
    compactFontSize: 0,
    compactGap: 0,
    hasOverflow: false,
    canScrollPrevious: false,
    canScrollNext: false,
  });

  useLayoutEffect(() => {
    if (isMobile) return;

    const wrapper = wrapperRef.current;
    const container = scrollRef.current;
    const content = contentRef.current;
    const regularMeasure = regularMeasureRef.current;
    const compactMeasure = compactMeasureRef.current;
    if (!wrapper || !container || !content || !regularMeasure || !compactMeasure) {
      return;
    }

    const updateScrollState = () => {
      const availableWidth = wrapper.clientWidth;
      const compactItem = compactMeasure.firstElementChild;
      if (!availableWidth || !compactItem) return;

      // Эталонные строки сохраняют размеры независимо от размера видимых кнопок.
      const regularWidth = regularMeasure.getBoundingClientRect().width;
      const compactWidth = compactMeasure.getBoundingClientRect().width;
      const itemStyle = getComputedStyle(compactItem);
      const basePaddingInline = Number.parseFloat(itemStyle.paddingLeft);
      const baseFontSize = Number.parseFloat(itemStyle.fontSize);
      const baseGap = Number.parseFloat(getComputedStyle(compactMeasure).columnGap);
      const isCompact = regularWidth > availableWidth;
      const hasOverflow =
        isCompact && itemCount > PRICE_FILTER_SLIDER_THRESHOLD && compactWidth > availableWidth;
      let compactPaddingInline = basePaddingInline;
      let compactFontSize = baseFontSize;
      let compactGap = baseGap;

      if (isCompact && !hasOverflow && compactWidth > availableWidth) {
        const fitWidth = availableWidth - ROW_FIT_RESERVE_PX;
        const widthWithoutPadding = compactWidth - basePaddingInline * 2 * itemCount;
        compactPaddingInline = Math.max(0, (fitWidth - widthWithoutPadding) / (2 * itemCount));

        if (widthWithoutPadding > fitWidth) {
          const gapCount = itemCount - 1;
          if (gapCount > 0) {
            compactGap = Math.max(
              MIN_COMPACT_GAP_PX,
              baseGap - (widthWithoutPadding - fitWidth) / gapCount
            );
          }
          const removedSpacing =
            basePaddingInline * 2 * itemCount + (baseGap - compactGap) * gapCount;

          if (compactWidth - removedSpacing > fitWidth) {
            let minFontSize = 0;
            let maxFontSize = baseFontSize;
            const originalFontSize = compactMeasure.style.fontSize;
            try {
              for (let iteration = 0; iteration < FONT_FIT_ITERATIONS; iteration++) {
                const candidate = (minFontSize + maxFontSize) / 2;
                compactMeasure.style.fontSize = `${candidate}px`;
                const measuredWidth = compactMeasure.getBoundingClientRect().width - removedSpacing;
                if (measuredWidth <= fitWidth) minFontSize = candidate;
                else maxFontSize = candidate;
              }
              compactFontSize = minFontSize;
            } finally {
              compactMeasure.style.fontSize = originalFontSize;
            }
          }
        }
      }

      const nextState = {
        isCompact,
        compactPaddingInline,
        compactFontSize,
        compactGap,
        hasOverflow,
        canScrollPrevious: hasOverflow && container.scrollLeft > SCROLL_EDGE_TOLERANCE_PX,
        canScrollNext:
          hasOverflow &&
          container.scrollWidth - container.clientWidth - container.scrollLeft >
            SCROLL_EDGE_TOLERANCE_PX,
      };

      setScrollState(current =>
        current.isCompact === nextState.isCompact &&
        current.compactPaddingInline === nextState.compactPaddingInline &&
        current.compactFontSize === nextState.compactFontSize &&
        current.compactGap === nextState.compactGap &&
        current.hasOverflow === nextState.hasOverflow &&
        current.canScrollPrevious === nextState.canScrollPrevious &&
        current.canScrollNext === nextState.canScrollNext
          ? current
          : nextState
      );
    };

    updateScrollState();
    const observer = new ResizeObserver(updateScrollState);
    observer.observe(wrapper);
    observer.observe(container);
    observer.observe(content);
    observer.observe(regularMeasure);
    observer.observe(compactMeasure);
    container.addEventListener('scroll', updateScrollState);

    return () => {
      observer.disconnect();
      container.removeEventListener('scroll', updateScrollState);
    };
  }, [isMobile, itemCount]);

  const scrollByPage = (direction: number) => {
    const container = scrollRef.current;
    if (!container) return;

    container.scrollBy({
      left: direction * container.clientWidth,
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches
        ? 'instant'
        : 'smooth',
    });
  };

  return {
    wrapperRef,
    scrollRef,
    contentRef,
    regularMeasureRef,
    compactMeasureRef,
    scrollByPage,
    ...scrollState,
  };
}
